import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";

const MODE_CONFIG = {
  APPROVE: { title: "Approve Request", decision: "APPROVED", required: false, placeholder: "Note (optional)", buttonLabel: "Approve", buttonClass: "bg-emerald-600 hover:bg-emerald-700" },
  APPROVE_WITH_CONDITIONS: { title: "Approve with Conditions", decision: "PARTIALLY_APPROVED", required: true, placeholder: "Describe the conditions (required)", buttonLabel: "Approve with Conditions", buttonClass: "bg-amber-600 hover:bg-amber-700" },
  DECLINE: { title: "Decline Request", decision: "DECLINED", required: true, placeholder: "Reason for declining (required)", buttonLabel: "Decline", buttonClass: "bg-red-600 hover:bg-red-700" },
  CLARIFY: { title: "Request Clarification", decision: null, required: true, placeholder: "What do you need clarified? (required)", buttonLabel: "Send Request", buttonClass: "bg-blue-600 hover:bg-blue-700" },
};

/** Replaces the window.prompt()-based Availability Review decisions. `CLARIFY` doesn't map to
 * a decision status (there is no backend status for "asked a question") - it keeps the
 * request in UNDER_REVIEW and stores the message via `availability_service.request_clarification`
 * so it is never confused with an actual APPROVED/PARTIALLY_APPROVED/DECLINED decision. */
export const AvailabilityDecisionDialog = ({ open, onOpenChange, mode, request, onDecide, onClarify }) => {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const config = mode ? MODE_CONFIG[mode] : null;

  useEffect(() => { if (open) setNote(""); }, [open, mode]);

  if (!config || !request) return null;

  const handleSubmit = async () => {
    if (config.required && !note.trim()) return;
    setSubmitting(true);
    try {
      if (mode === "CLARIFY") await onClarify(request.id, note.trim());
      else await onDecide(request.id, config.decision, note.trim() || null);
      onOpenChange(false);
    } catch (e) {
      // Error already surfaced via toast by the parent action helper.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-md" data-testid={`availability-decision-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>
            {request.request_type === "TIME_OFF" ? "Day off request" : "Availability submission"} - {request.target_month}{request.date ? ` - ${request.date}` : ""}
          </DialogDescription>
        </DialogHeader>
        <Textarea data-testid={`availability-decision-note-${mode}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={config.placeholder} rows={3} />
        <DialogFooter>
          <button data-testid={`availability-decision-cancel-${mode}`} onClick={() => onOpenChange(false)} disabled={submitting} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50">Cancel</button>
          <button
            data-testid={`availability-decision-confirm-${mode}`}
            disabled={submitting || (config.required && !note.trim())}
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 ${config.buttonClass}`}
          >
            {submitting ? "Saving..." : config.buttonLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
